import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users2, TrendingUp, Ban, ArrowRightLeft, ChevronRight, ChevronDown, Copy, CheckCircle2 } from "lucide-react";

export default function MinistryPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { nav("/login?next=/ministry"); return; }
    api.get("/me/bulk-bundles").then(r => { setBundles(r.data); setLoading(false); })
      .catch(() => { toast.error("Failed to load bundles"); setLoading(false); });
  }, [user, nav]);

  if (loading) return <div className="pt-24 text-center text-white/60">Loading Enterprise Ministry Portal…</div>;

  return (
    <div className="pt-16 min-h-screen" data-testid="ministry-portal-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2">Enterprise Ministry Portal</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-3">Seat Management</h1>
        <p className="text-white/60 max-w-3xl mb-10">Manage every seat in your bulk-gifted congregation — see who's redeemed, track engagement, revoke abandoned codes, or reassign to new recipients. Individual codes are never exposed to viewers.</p>

        {bundles.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-[#0A0A0E] p-12 text-center">
            <p className="text-white/70 mb-4">You haven't purchased any bulk-gift bundles yet.</p>
            <button onClick={() => nav("/bulk-gift")} className="btn-gold px-6 py-2.5 rounded-full text-sm font-semibold" data-testid="cta-purchase-bundle">Purchase a Ministry Bundle →</button>
          </div>
        ) : (
          <div className="space-y-4">
            {bundles.map(b => (
              <BundleRow key={b.id} bundle={b} expanded={selected === b.id} onToggle={() => setSelected(selected === b.id ? null : b.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BundleRow({ bundle, expanded, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [seats, eng] = await Promise.all([
        api.get(`/ministry/bundles/${bundle.id}/seats`),
        api.get(`/ministry/bundles/${bundle.id}/engagement`),
      ]);
      setDetail(seats.data); setEngagement(eng.data);
    } catch (e) { toast.error("Failed to load seats"); }
  };

  useEffect(() => { if (expanded && !detail) load(); }, [expanded]);

  const revoke = async (code_id) => {
    if (!window.confirm("Revoke this seat? If already redeemed, the recipient will lose access.")) return;
    setBusy(true);
    try { await api.post(`/ministry/bundles/${bundle.id}/seats/${code_id}/revoke`, { reason: "revoked_via_portal" }); toast.success("Seat revoked"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); } finally { setBusy(false); }
  };

  const reassign = async (code_id) => {
    const email = window.prompt("New recipient email:")?.trim();
    if (!email) return;
    const name = window.prompt("Recipient name (optional):")?.trim() || null;
    setBusy(true);
    try { await api.post(`/ministry/bundles/${bundle.id}/seats/${code_id}/reassign`, { new_recipient_email: email, new_recipient_name: name }); toast.success("Seat reassigned"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); } finally { setBusy(false); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Code copied"); } catch {}
  };

  const pct = detail ? Math.round(detail.summary.redemption_rate * 100) : 0;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0E] overflow-hidden" data-testid={`bundle-row-${bundle.id}`}>
      <button onClick={onToggle} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/[0.02] transition" data-testid={`bundle-toggle-${bundle.id}`}>
        <div className="flex items-center gap-5">
          <Users2 className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <p className="font-heading text-lg text-white">{bundle.ministry_name || `Bundle #${bundle.tier}`}</p>
            <p className="text-xs text-white/50 mt-0.5">{bundle.seats} seats · {bundle.months} month(s) each · Status: <span className={bundle.status === "paid" ? "text-emerald-400" : "text-amber-400"}>{bundle.status}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-[10px] tracking-widest uppercase text-white/40">Redeemed</p>
            <p className="text-lg font-bold text-white">{bundle.redeemed_count ?? 0}<span className="text-white/40 text-sm"> / {bundle.seats}</span></p>
          </div>
          {expanded ? <ChevronDown className="w-5 h-5 text-white/60"/> : <ChevronRight className="w-5 h-5 text-white/60"/>}
        </div>
      </button>

      {expanded && detail && (
        <div className="border-t border-white/10 p-6 space-y-6">
          {/* Summary */}
          <div className="grid md:grid-cols-4 gap-3">
            <Kpi label="Redemption Rate" value={`${pct}%`} testid="kpi-rate"/>
            <Kpi label="Available" value={detail.summary.available} testid="kpi-available"/>
            <Kpi label="Revoked" value={detail.summary.revoked} testid="kpi-revoked"/>
            <Kpi label="Total Watch Minutes" value={engagement?.total_minutes ?? 0} testid="kpi-minutes"/>
          </div>

          {/* Top watchers */}
          {engagement?.top_watchers?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#D4AF37]"/><p className="text-[10px] tracking-widest uppercase text-white/60">Top Watchers (30d)</p></div>
              <div className="border border-white/10 rounded-lg divide-y divide-white/5">
                {engagement.top_watchers.slice(0,5).map(w => (
                  <div key={w.user_id} className="p-3 flex items-center justify-between text-sm" data-testid={`top-watcher-${w.user_id}`}>
                    <div><p className="text-white/90">{w.name || w.email}</p><p className="text-xs text-white/40">{w.email}</p></div>
                    <div className="text-right"><p className="text-white font-semibold">{w.minutes} min</p><p className="text-xs text-white/40">{w.items} items</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Seats table */}
          <div>
            <p className="text-[10px] tracking-widest uppercase text-white/60 mb-2">All Seats ({detail.seats.length})</p>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-white/[0.03] text-[10px] font-semibold tracking-widest uppercase text-white/50">
                <div className="col-span-3">Code / Recipient</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-3">Redeemer</div>
                <div className="col-span-2">Engagement (30d)</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
                {detail.seats.map(s => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-xs items-center hover:bg-white/[0.02]" data-testid={`seat-row-${s.id}`}>
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <code className="text-[#D4AF37] font-mono">{s.code}</code>
                        <button onClick={() => copyCode(s.code)} className="text-white/40 hover:text-white" data-testid={`copy-code-${s.id}`}><Copy className="w-3 h-3"/></button>
                      </div>
                      {s.recipient_email && <p className="text-white/40 mt-0.5">→ {s.recipient_email}</p>}
                    </div>
                    <div className="col-span-2">
                      <StatusPill status={s.status} redeemed={!!s.redeemed_by_user_id}/>
                    </div>
                    <div className="col-span-3">
                      {s.redeemer_email ? (
                        <>
                          <p className="text-white/90">{s.redeemer_name || s.redeemer_email}</p>
                          <p className="text-white/40">Ends: {s.redeemer_premium_end ? new Date(s.redeemer_premium_end).toLocaleDateString() : "—"}</p>
                        </>
                      ) : <p className="text-white/30">—</p>}
                    </div>
                    <div className="col-span-2">
                      {s.engagement_minutes_30d !== undefined ? (
                        <p className="text-white/80">{s.engagement_minutes_30d} min<span className="text-white/40"> · {s.watch_items} items</span></p>
                      ) : <p className="text-white/30">—</p>}
                    </div>
                    <div className="col-span-2 flex items-center gap-1 justify-end">
                      {s.status !== "revoked" && !s.redeemed_by_user_id && (
                        <button disabled={busy} onClick={() => reassign(s.id)} className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-40" title="Reassign" data-testid={`reassign-${s.id}`}><ArrowRightLeft className="w-3.5 h-3.5"/></button>
                      )}
                      {s.status !== "revoked" && (
                        <button disabled={busy} onClick={() => revoke(s.id)} className="p-1.5 rounded hover:bg-red-500/20 text-white/60 hover:text-red-400 disabled:opacity-40" title="Revoke" data-testid={`revoke-${s.id}`}><Ban className="w-3.5 h-3.5"/></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-4" data-testid={testid}>
      <p className="text-[10px] tracking-widest uppercase text-white/50">{label}</p>
      <p className="font-heading text-2xl font-bold text-white mt-1">{value}</p>
    </div>
  );
}

function StatusPill({ status, redeemed }) {
  const map = {
    redeemed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    paid: redeemed ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-sky-500/15 text-sky-400 border-sky-500/30",
    revoked: "bg-red-500/15 text-red-400 border-red-500/30",
    pending_payment: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  };
  const label = redeemed ? "redeemed" : status;
  return <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${map[label] || map.paid}`}>{label.replace('_',' ')}</span>;
}
